import { useTheme } from "@emotion/react";
import {
  AddCircleOutline,
  DeleteOutline,
  ExpandMore,
  GroupOutlined,
  InfoOutlined,
  PhoneOutlined,
  TextsmsOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import api from "../../../../../../services/api";
import ErpChip from "../../../../../../components/erp/ErpChip";
import ErpFilter from "../../../../../../components/erp/ErpFilter";
import useErpSelecionado from "../../../../../../components/erp/useErpSelecionado";
import VirtualizedAutocomplete from "../../../../../../components/virtualization/virtualizedAutocomplete";
import useStyles from "./styles";

export default function ConfigOpenTicket() {
  const theme = useTheme();
  const styles = useStyles(theme);
  // Origens, tipos, status, responsáveis e etapas são IDs de cada HubSoft
  // (HUBSOFT A e B têm a sua configuração): a tela mostra e salva a do ERP
  // escolhido, e as opções vêm dele.
  const { erps, erpId, setErpId, carregado, variosErps } = useErpSelecionado([
    "HUBSOFT",
  ]);
  const erpSelecionado = erps?.find((item) => item.id === erpId);
  const erpAtualRef = useRef(erpId);
  erpAtualRef.current = erpId;
  // Busca só depois da primeira abertura do accordion (e de novo ao trocar de ERP).
  const [jaAberto, setJaAberto] = useState(false);

  const [loading, setLoading] = useState(false);
  const [paramsOpenTicketCliente, setParamsOpenTicketCliente] = useState([]);
  const [error, setError] = useState(false);
  const [tipoAtendimento, setTipoAtendimento] = useState([]);
  const [loadingParams, setLoadingParams] = useState(false);
  const [origemContato, setOrigemContato] = useState([]);
  const [userResponsaveis, setUserResponsaveis] = useState([]);
  const [etapasFluxoAtendimento, setEtapasFluxoAtendimento] = useState([]);
  const [statusAtendimento, setStatusAtendimento] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [configBackupChange, setConfigBackupChange] = useState([]);
  const [allPermissoes, setAllPermissoes] = useState([]);
  const [selectedPermissao, setSelectedPermissao] = useState(null);

  useEffect(() => {
    if (!carregado || !jaAberto) return;
    setIsEdit(false);
    setParamsOpenTicketCliente([]);
    fetchAllData();
  }, [carregado, erpId, jaAberto]);

  useEffect(() => {
    if (!selectedPermissao && allPermissoes?.length > 0) {
      setSelectedPermissao(allPermissoes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPermissoes]);

  const handleAddConfig = () => {
    if (!selectedPermissao) return;

    setParamsOpenTicketCliente((prev) => [
      ...prev,
      {
        id: null,
        permissaoId: selectedPermissao.id,
        origemContatoId: null,
        tipoAtendimentoId: null,
        statusAtendimentoId: null,
        usuariosResponsaveis: [],
        idEtapaFluxoAtendimento: "",
        forms: [], // Inicializa com a propriedade de formulários vazia
      },
    ]);
  };

  const handleRemoveConfig = (indexToRemove) => {
    setParamsOpenTicketCliente((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleChangeField = (index, field, value) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      updatedList[index] = {
        ...updatedList[index],
        [field]: value,
      };
      return updatedList;
    });
  };

  // --- NOVAS FUNÇÕES PARA GERENCIAMENTO DO FORMULÁRIO DINÂMICO ---
  const handleAddFormField = (configIndex) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      const currentForms = updatedList[configIndex].forms || [];
      updatedList[configIndex] = {
        ...updatedList[configIndex],
        forms: [...currentForms, { name: "", type: 0, description: "" }], // Tipo 0 (campo livre) por padrão
      };
      return updatedList;
    });
  };

  const handleRemoveFormField = (configIndex, formFieldIndex) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      updatedList[configIndex] = {
        ...updatedList[configIndex],
        forms: updatedList[configIndex].forms.filter(
          (_, i) => i !== formFieldIndex,
        ),
      };
      return updatedList;
    });
  };

  const handleChangeFormField = (
    configIndex,
    formFieldIndex,
    property,
    value,
  ) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      const updatedForms = [...updatedList[configIndex].forms];
      updatedForms[formFieldIndex] = {
        ...updatedForms[formFieldIndex],
        [property]: value,
      };
      updatedList[configIndex] = {
        ...updatedList[configIndex],
        forms: updatedForms,
      };
      return updatedList;
    });
  };
  // ---------------------------------------------------------------

  // A rota de busca retorna `permissaoId` na raiz do item, mas a rota de
  // criação (ainda) não devolve esse campo — nesse caso, recuperamos o
  // permissaoId a partir da lista que foi enviada na mesma posição.
  const formatConfigList = (data, sourceList = []) => {
    if (!Array.isArray(data)) return [];
    return data
      .map((item, index) => {
        if (!item?.config?.paramsOpenTicketCliente) return null;
        return {
          ...item.config.paramsOpenTicketCliente,
          id: item.id,
          forms: item?.forms || [],
          permissaoId:
            item?.permissaoId ?? sourceList?.[index]?.permissaoId ?? null,
        };
      })
      .filter(Boolean);
  };

  const createConfig = async () => {
    setError(false);

    const hasEmptyFields = paramsOpenTicketCliente.some((item) => {
      return (
        !item.permissaoId ||
        !item.tipoAtendimentoId ||
        !item.statusAtendimentoId ||
        !item.origemContatoId ||
        !item.usuariosResponsaveis ||
        item.usuariosResponsaveis.length === 0
      );
    });

    // Validação extra opcional: Se houver um campo de form adicionado, ele deve ter um nome
    const hasEmptyFormNames = paramsOpenTicketCliente.some((item) =>
      item.forms?.some((formField) => !formField.name.trim()),
    );

    if (hasEmptyFields || hasEmptyFormNames) {
      setError(true);
      setSnackbar({
        children: hasEmptyFormNames
          ? "Por favor, dê um nome para todos os campos de formulário criados."
          : "Por favor, preencha todos os campos obrigatórios de todas as configurações.",
        severity: "error",
      });
      return;
    }

    // A combinação Permissão + Tipo de Atendimento não pode se repetir
    const duplicateCounts = {};
    paramsOpenTicketCliente.forEach((item) => {
      const key = `${item.permissaoId}::${item.tipoAtendimentoId}`;
      duplicateCounts[key] = (duplicateCounts[key] || 0) + 1;
    });
    const hasDuplicate = Object.values(duplicateCounts).some(
      (count) => count > 1,
    );

    if (hasDuplicate) {
      setSnackbar({
        children:
          "Não é permitido salvar mais de uma configuração com o mesmo Tipo de Atendimento dentro da mesma permissão.",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/interno/create/configTicket", {
        erpId,
        listaConfigs: paramsOpenTicketCliente,
      });

      setSnackbar({
        children:
          "Configurações de abertura de ticket atualizadas com sucesso!",
        severity: "success",
      });

      if (Array.isArray(response?.data)) {
        setParamsOpenTicketCliente(
          formatConfigList(response.data, paramsOpenTicketCliente),
        );
      }

      setIsEdit(false);
    } catch (error) {
      console.error("Erro ao criar configuração do cliente:", error);
      setSnackbar({
        children: "Erro ao criar configuração de ticket!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getConfigTicketCliente = async () => {
    const erpPedido = erpId;
    try {
      const response = await api.post("/interno/find/configTicket", {
        erpId: erpPedido,
        tipoConfig: 0,
      });
      if (erpPedido !== erpAtualRef.current) return; // trocou de ERP no meio

      if (Array.isArray(response?.data)) {
        setParamsOpenTicketCliente(formatConfigList(response.data));
      } else if (response?.data?.config?.paramsOpenTicketCliente) {
        const singleConfig = response.data.config.paramsOpenTicketCliente;
        const singleForms = response.data.forms || [];

        const mappedSingle = {
          ...singleConfig,
          id: response.data.id,
          forms: singleForms || [],
          permissaoId: response.data?.permissaoId ?? null,
        };
        setParamsOpenTicketCliente([mappedSingle]);
      }
    } catch (error) {
      console.error("Erro ao buscar configuração do cliente:", error);
      setSnackbar({
        children: "Erro ao buscar configuração de ticket!",
        severity: "error",
      });
    }
  };

  const getParamsEditTicket = async () => {
    const erpPedido = erpId;
    setLoadingParams(true);
    try {
      const response = await api.post("/interno/find/paramsNewTicket", {
        erpId: erpPedido,
      });
      if (erpPedido !== erpAtualRef.current) return; // trocou de ERP no meio
      setUserResponsaveis(response?.data?.userResponsaveis || []);
      setTipoAtendimento(response?.data?.tipoAtendimento || []);
      setOrigemContato(response?.data?.origemContato || []);
      const statusAtendimentoFiltered =
        response?.data?.statusAtendimento?.filter((item) => item?.id !== 3) ||
        [];
      setStatusAtendimento(statusAtendimentoFiltered);
      setEtapasFluxoAtendimento(response?.data?.etapasFluxoAtendimento || []);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    } finally {
      setLoadingParams(false);
    }
  };

  const getPermissoes = async () => {
    setLoadingParams(true);
    try {
      const response = await api.get("/find/allPermissoesEdit");
      setAllPermissoes(response?.data || []);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    } finally {
      setLoadingParams(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      getParamsEditTicket(),
      getConfigTicketCliente(),
      getPermissoes(),
    ]);
    setLoading(false);
  };

  const handleEdit = () => {
    setIsEdit(true);
    setConfigBackupChange(JSON.parse(JSON.stringify(paramsOpenTicketCliente)));
  };

  const handleCancel = () => {
    setIsEdit(false);
    setError(false);
    setParamsOpenTicketCliente(configBackupChange);
  };

  // Configurações (com seu índice original na lista completa) pertencentes à permissão selecionada
  const configsDaPermissaoSelecionada = paramsOpenTicketCliente
    ?.map((item, originalIndex) => ({ item, originalIndex }))
    ?.filter(({ item }) => item.permissaoId === selectedPermissao?.id);

  return (
    <>
      <Accordion data-secao="abertura-permissao" onChange={(_, aberto) => aberto && setJaAberto(true)}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={styles.headerTitleBox}>
              <TuneOutlined color="primary" />
              <Typography variant="h5" sx={styles.title}>
                Configurações de abertura de ticket por permissão
              </Typography>
              {/* Para qual ERP é esta configuração. */}
              <ErpChip erp={erpSelecionado || { tipo: "HUBSOFT" }} />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {variosErps && (
            <ErpFilter
              erps={erps}
              value={erpId}
              onChange={(id) => id && setErpId(id)}
              limpavel={false}
              label="HubSoft"
              id="filtro-erp-abertura-ticket-permissao"
              sx={{ minWidth: 260, maxWidth: 360, mb: 1.5 }}
            />
          )}
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={styles.boxLoading}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={styles.boxMainConfig}>
                {/* SELEÇÃO DA PERMISSÃO A SER CONFIGURADA */}
                <Paper variant="outlined" sx={styles.permissaoSelectorPaper}>
                  <Box sx={styles.permissaoSelectorHeaderBox}>
                    <GroupOutlined color="primary" fontSize="small" />
                    <Typography data-busca="ap-permissao" variant="subtitle1" fontWeight="bold">
                      Permissão a ser configurada
                    </Typography>
                  </Box>
                  <Typography sx={styles.subtitle}>
                    Define como os usuários com esta permissão abrirão tickets.
                    Aceita múltiplas configurações se os Tipos de Atendimento
                    forem distintos. Sem configuração, a abertura exigirá o
                    fluxo completo (todas as etapas).
                  </Typography>
                  <Box sx={styles.permissaoSelectorContainer}>
                    <VirtualizedAutocomplete
                      size="small"
                      options={allPermissoes}
                      value={selectedPermissao}
                      loading={loadingParams}
                      disabled={loadingParams}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) =>
                        option
                          ? `${option?.codigoPermissao || option?.descricao || ""}`
                          : ""
                      }
                      onChange={(event, value) => setSelectedPermissao(value)}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={styles.permissaoOption}
                        >
                          <Box
                            sx={{
                              ...styles.permissaoColorDot,
                              bgcolor: option.color,
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {option.codigoPermissao}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.descricao}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      sx={styles.permissaoAutocomplete}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          variant="filled"
                          label="Selecione a permissão"
                        />
                      )}
                    />
                    {selectedPermissao && (
                      <Chip
                        label={`Configurando: ${selectedPermissao?.codigoPermissao}`}
                        sx={{
                          ...styles.chipSelectedPermissao,
                          bgcolor: selectedPermissao?.color,
                        }}
                      />
                    )}
                  </Box>
                </Paper>

                {!selectedPermissao ? (
                  <Box sx={styles.boxEmptyState}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={styles.textItalic}
                    >
                      Selecione uma permissão acima para visualizar ou
                      configurar as regras de abertura de ticket.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {configsDaPermissaoSelecionada?.length > 0 ? (
                      configsDaPermissaoSelecionada?.map(
                        ({ item: configItem, originalIndex }, displayIndex) => (
                          <Paper
                            key={configItem?.id ?? originalIndex}
                            sx={styles.paperItemConfig}
                            variant="outlined"
                          >
                            <Box sx={styles.boxTitleConfig}>
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                color="primary"
                              >
                                Configuração #{displayIndex + 1}
                              </Typography>
                              {isEdit && (
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleRemoveConfig(originalIndex)
                                  }
                                >
                                  <DeleteOutline />
                                </IconButton>
                              )}
                            </Box>

                            {/* 1. TIPO DE ATENDIMENTO */}
                            <Box sx={styles.containerInfoConfig}>
                              <VirtualizedAutocomplete
                                size="small"
                                options={tipoAtendimento}
                                value={
                                  tipoAtendimento?.find(
                                    (item) =>
                                      item?.id ===
                                      configItem?.tipoAtendimentoId,
                                  ) || null
                                }
                                getOptionLabel={(option) =>
                                  option ? `${option?.descricao || ""} ` : ""
                                }
                                disabled={loadingParams || !isEdit}
                                onChange={(event, value) =>
                                  handleChangeField(
                                    originalIndex,
                                    "tipoAtendimentoId",
                                    value?.id || null,
                                  )
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                sx={styles.autocompleteInfoConfig}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    variant="filled"
                                    error={
                                      error && !configItem?.tipoAtendimentoId
                                    }
                                    helperText={
                                      error &&
                                      !configItem?.tipoAtendimentoId &&
                                      "Campo obrigatório"
                                    }
                                    label="Tipo de atendimento"
                                    data-busca="ap-tipo"
                                  />
                                )}
                              />
                              <Paper
                                variant="outlined"
                                sx={styles.paperDescription}
                              >
                                <Typography sx={styles.subtitle}>
                                  Selecione qual tipo de atendimento deve ser
                                  atribuída ao ticket aberto pelo cliente.
                                </Typography>
                              </Paper>
                            </Box>

                            {/* 2. STATUS DO ATENDIMENTO */}
                            <Box sx={styles.containerInfoConfig}>
                              <VirtualizedAutocomplete
                                size="small"
                                options={statusAtendimento}
                                value={
                                  statusAtendimento?.find(
                                    (item) =>
                                      item?.id ===
                                      configItem?.statusAtendimentoId,
                                  ) || null
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                getOptionLabel={(option) =>
                                  option ? `${option?.descricao || ""} ` : ""
                                }
                                disabled={loadingParams || !isEdit}
                                onChange={(event, value) =>
                                  handleChangeField(
                                    originalIndex,
                                    "statusAtendimentoId",
                                    value?.id || null,
                                  )
                                }
                                sx={styles.autocompleteInfoConfig}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    variant="filled"
                                    error={
                                      error && !configItem?.statusAtendimentoId
                                    }
                                    helperText={
                                      error &&
                                      !configItem?.statusAtendimentoId &&
                                      "Campo obrigatório"
                                    }
                                    label="Status do atendimento"
                                    data-busca="ap-status"
                                  />
                                )}
                              />
                              <Paper
                                variant="outlined"
                                sx={styles.paperDescription}
                              >
                                <Typography sx={styles.subtitle}>
                                  Selecione qual status que deve ser atribuída
                                  ao ticket aberto pelo cliente.
                                </Typography>
                              </Paper>
                            </Box>

                            {/* 3. ORIGEM DO CONTATO */}
                            <Box sx={styles.containerInfoConfig}>
                              <VirtualizedAutocomplete
                                size="small"
                                options={origemContato}
                                value={
                                  origemContato?.find(
                                    (item) =>
                                      item?.idOrigemContato ===
                                      configItem?.origemContatoId,
                                  ) || null
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.idOrigemContato ===
                                  value.idOrigemContato
                                }
                                getOptionLabel={(option) =>
                                  option
                                    ? `${option?.origemContato || ""} `
                                    : ""
                                }
                                onChange={(event, value) =>
                                  handleChangeField(
                                    originalIndex,
                                    "origemContatoId",
                                    value?.idOrigemContato || null,
                                  )
                                }
                                sx={styles.autocompleteInfoConfig}
                                disabled={loadingParams || !isEdit}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    variant="filled"
                                    error={
                                      error && !configItem?.origemContatoId
                                    }
                                    helperText={
                                      error &&
                                      !configItem?.origemContatoId &&
                                      "Campo obrigatório"
                                    }
                                    label="Origem do contato"
                                    data-busca="ap-origem"
                                  />
                                )}
                              />
                              <Paper
                                variant="outlined"
                                sx={styles.paperDescription}
                              >
                                <Typography sx={styles.subtitle}>
                                  Selecione qual origem de contato deve ser
                                  atribuída ao ticket aberto pelo cliente.
                                </Typography>
                              </Paper>
                            </Box>

                            {/* 4. USUÁRIO RESPONSÁVEL */}
                            <Box sx={styles.containerInfoConfig}>
                              <VirtualizedAutocomplete
                                multiple
                                limitTags={1}
                                size="small"
                                options={userResponsaveis}
                                disabled={loadingParams || !isEdit}
                                value={configItem?.usuariosResponsaveis || []}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                onChange={(event, value) =>
                                  handleChangeField(
                                    originalIndex,
                                    "usuariosResponsaveis",
                                    value || [],
                                  )
                                }
                                sx={styles.autocompleteInfoConfig}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={
                                      error &&
                                      (!configItem?.usuariosResponsaveis ||
                                        configItem?.usuariosResponsaveis
                                          ?.length === 0)
                                    }
                                    helperText={
                                      error &&
                                      (!configItem?.usuariosResponsaveis ||
                                        configItem?.usuariosResponsaveis
                                          ?.length === 0) &&
                                      "Campo obrigatório"
                                    }
                                    size="small"
                                    variant="filled"
                                    label="Usuário responsável"
                                    data-busca="ap-responsavel"
                                  />
                                )}
                              />
                              <Paper
                                variant="outlined"
                                sx={styles.paperDescription}
                              >
                                <Typography sx={styles.subtitle}>
                                  Selecione quais usuários responsáveis devem
                                  ser atribuídos ao ticket aberto pelo cliente.
                                </Typography>
                              </Paper>
                            </Box>

                            {/* 5. ETAPA FLUXO ATENDIMENTO */}
                            <Box sx={styles.containerInfoConfig}>
                              <VirtualizedAutocomplete
                                size="small"
                                options={etapasFluxoAtendimento}
                                disabled={loadingParams || !isEdit}
                                value={
                                  etapasFluxoAtendimento.find(
                                    (item) =>
                                      item.idEtapaFluxoAtendimento ===
                                      configItem?.idEtapaFluxoAtendimento,
                                  ) || null
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.idEtapaFluxoAtendimento ===
                                  value.idEtapaFluxoAtendimento
                                }
                                getOptionLabel={(option) =>
                                  option?.etapaFluxoAtendimento || ""
                                }
                                onChange={(event, value) =>
                                  handleChangeField(
                                    originalIndex,
                                    "idEtapaFluxoAtendimento",
                                    value?.idEtapaFluxoAtendimento || "",
                                  )
                                }
                                sx={styles.autocompleteInfoConfig}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    variant="filled"
                                    label="Etapa fluxo atendimento"
                                    data-busca="ap-etapa"
                                  />
                                )}
                              />
                              <Paper
                                variant="outlined"
                                sx={styles.paperDescription}
                              >
                                <Typography sx={styles.subtitle}>
                                  Selecione a etapa do fluxo de atendimento que
                                  o ticket aberto pelo cliente deve iniciar.
                                </Typography>
                              </Paper>
                            </Box>

                            {/* --- CAMPOS ADICIONAIS DO FORMULÁRIO --- */}
                            <Paper variant="outlined" sx={styles.paperForm}>
                              <Box sx={styles.boxPaperForm}>
                                <Box sx={styles.boxFormsHeaderText}>
                                  <Typography
                                    data-busca="ap-campos"
                                    variant="subtitle2"
                                    fontWeight="bold"
                                  >
                                    Campos Personalizados do Formulário
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Opcional: Crie formulário personalizado para
                                    o cliente preencher na abertura do ticket
                                  </Typography>
                                </Box>
                                {isEdit && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddCircleOutline />}
                                    onClick={() =>
                                      handleAddFormField(originalIndex)
                                    }
                                  >
                                    Adicionar Campo
                                  </Button>
                                )}
                              </Box>

                              {configItem?.forms &&
                              configItem?.forms?.length > 0 ? (
                                <Box sx={styles.campoForms}>
                                  {configItem?.forms?.map(
                                    (formField, formFieldIndex) => (
                                      <Box
                                        key={formFieldIndex}
                                        sx={styles.boxTextfieldForm}
                                      >
                                        <Box sx={styles.boxFormFieldHeader}>
                                          <Typography
                                            sx={styles.formFieldIndexLabel}
                                          >
                                            Campo {formFieldIndex + 1}
                                          </Typography>
                                        </Box>
                                        <Box sx={styles.formFieldTopRow}>
                                          <TextField
                                            size="small"
                                            label="Nome do campo (Ex: Responsável)"
                                            variant="filled"
                                            value={formField.name}
                                            disabled={!isEdit}
                                            error={
                                              error && !formField.name.trim()
                                            }
                                            onChange={(e) =>
                                              handleChangeFormField(
                                                originalIndex,
                                                formFieldIndex,
                                                "name",
                                                e.target.value,
                                              )
                                            }
                                            sx={styles.formFieldName}
                                          />

                                          <FormControl
                                            variant="filled"
                                            size="small"
                                            sx={styles.formFieldType}
                                          >
                                            <InputLabel
                                              id={`select-type-label-${originalIndex}-${formFieldIndex}`}
                                            >
                                              Tipo
                                            </InputLabel>
                                            <Select
                                              size="small"
                                              labelId={`select-type-label-${originalIndex}-${formFieldIndex}`}
                                              label="Tipo"
                                              value={formField.type}
                                              disabled={!isEdit}
                                              onChange={(e) =>
                                                handleChangeFormField(
                                                  originalIndex,
                                                  formFieldIndex,
                                                  "type",
                                                  Number(e.target.value),
                                                )
                                              }
                                              renderValue={(selected) => (
                                                <Box sx={styles.boxIconSelect}>
                                                  {selected === 0 ? (
                                                    <TextsmsOutlined
                                                      fontSize="small"
                                                      color="action"
                                                    />
                                                  ) : (
                                                    <PhoneOutlined
                                                      fontSize="small"
                                                      color="action"
                                                    />
                                                  )}
                                                  <Typography variant="body2">
                                                    {selected === 0
                                                      ? "Campo Livre"
                                                      : "Telefone"}
                                                  </Typography>
                                                </Box>
                                              )}
                                            >
                                              <MenuItem value={0}>
                                                <Box sx={styles.boxIconForm}>
                                                  <TextsmsOutlined
                                                    fontSize="small"
                                                    color="action"
                                                  />
                                                  Campo Livre
                                                </Box>
                                              </MenuItem>
                                              <MenuItem value={1}>
                                                <Box sx={styles.boxIconForm}>
                                                  <PhoneOutlined
                                                    fontSize="small"
                                                    color="action"
                                                  />
                                                  Telefone
                                                </Box>
                                              </MenuItem>
                                            </Select>
                                          </FormControl>

                                          {isEdit && (
                                            <IconButton
                                              color="error"
                                              size="small"
                                              onClick={() =>
                                                handleRemoveFormField(
                                                  originalIndex,
                                                  formFieldIndex,
                                                )
                                              }
                                            >
                                              <DeleteOutline />
                                            </IconButton>
                                          )}
                                        </Box>

                                        {(isEdit || formField.description) && (
                                          <TextField
                                            size="small"
                                            variant="filled"
                                            label="Descrição auxiliar (opcional)"
                                            placeholder="Instruções de como preencher o campo"
                                            value={formField.description || ""}
                                            disabled={!isEdit}
                                            multiline
                                            maxRows={2}
                                            fullWidth
                                            onChange={(e) =>
                                              handleChangeFormField(
                                                originalIndex,
                                                formFieldIndex,
                                                "description",
                                                e.target.value,
                                              )
                                            }
                                            InputProps={{
                                              startAdornment: (
                                                <InfoOutlined
                                                  fontSize="small"
                                                  color="action"
                                                  sx={
                                                    styles.formFieldDescriptionIcon
                                                  }
                                                />
                                              ),
                                            }}
                                            sx={styles.formFieldDescription}
                                          />
                                        )}
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={styles.textItalicCenter}
                                >
                                  Nenhum campo personalizado adicionado a esta
                                  configuração.
                                </Typography>
                              )}
                            </Paper>
                            {/* -------------------------------------------------- */}
                          </Paper>
                        ),
                      )
                    ) : (
                      <Box sx={styles.boxEmptyState}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={styles.textItalic}
                        >
                          SEM CONFIGURAÇÃO PARA A PERMISSÃO{" "}
                          {selectedPermissao?.codigoPermissao}
                        </Typography>
                      </Box>
                    )}

                    {isEdit && (
                      <Button
                        variant="dashed"
                        onClick={handleAddConfig}
                        startIcon={<AddCircleOutline />}
                        sx={styles.buttonAddConfig}
                      >
                        Adicionar Nova Configuração para{" "}
                        {selectedPermissao?.codigoPermissao}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}
          </Paper>

          {!loading && (
            <Box sx={styles.boxActions}>
              {!isEdit ? (
                <Button variant="contained" size="small" onClick={handleEdit}>
                  Editar
                </Button>
              ) : (
                <Box sx={styles.boxEditActions}>
                  <Button
                    onClick={handleCancel}
                    size="small"
                    variant="outlined"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={createConfig}
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
